import { useEffect, useRef, type ReactNode } from 'react';
import Modal from './Modal.tsx';

type ConfirmModalProps = {
  title: ReactNode;
  body?: ReactNode;
  danger?: boolean;
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
  /** Optional middle-ground action (e.g. "delete the section but keep its
   *  slides") rendered between Cancel and the confirm button. Enter still
   *  confirms the primary. */
  secondaryLabel?: string | null;
  onSecondary?: (() => void) | null;
  busy?: boolean;
  /** Escape still cancels; Enter stops confirming. For a primary that NAVIGATES
   *  AWAY, where the keystroke that opened the dialog can carry straight into
   *  it — the staging warning opens on the Enter that sent a chat message, and
   *  a second Enter took the author to a different studio with their message
   *  unsent. A dialog that leaves the page wants two deliberate clicks. */
  enterConfirms?: boolean;
};

/**
 * Shared confirm-destructive dialog: the `Modal` shell with the
 * `.modal--confirm` modifier (smaller width, no eyebrow, smaller
 * display-serif title). Backdrop click cancels, Escape cancels, Enter
 * confirms; Cancel auto-focuses on mount.
 *
 * Enter is the one key this handles itself — Modal owns Escape, but no other
 * dialog confirms on Enter, and `enterConfirms` exists because some of these
 * must not.
 */
export default function ConfirmModal({
  title,
  body,
  danger = false,
  confirmLabel = 'Confirm',
  onCancel,
  onConfirm,
  // Optional middle-ground action (e.g. "delete the section but keep its
  // slides") rendered between Cancel and the confirm button. Enter still
  // confirms the primary.
  secondaryLabel = null,
  onSecondary = null,
  busy = false,
  enterConfirms = true,
}: ConfirmModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancelRef.current?.focus();
    if (!enterConfirms) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Enter') onConfirm();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onConfirm, enterConfirms]);

  return (
    <Modal
      title={title}
      onClose={onCancel}
      className="modal--confirm"
      backdropClassName="modal-backdrop--confirm"
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onCancel} ref={cancelRef} disabled={busy}>
            Cancel
          </button>
          {/* GHOST, not primary. The secondary is the middle-ground action
              ("Keep the slides", "Publish anyway"), and Enter fires the PRIMARY,
              so a secondary styled as the primary invites the click the dialog
              exists to slow down. */}
          {secondaryLabel && onSecondary && (
            <button type="button" className="btn btn--ghost" onClick={onSecondary} disabled={busy}>
              {secondaryLabel}
            </button>
          )}
          <button
            type="button"
            className={'btn ' + (danger ? 'btn--danger' : 'btn--primary')}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {body}
    </Modal>
  );
}
